import { useTranslation } from 'react-i18next'
import {
  CurecallButton,
  CurecallInput,
  CurecallInputError,
  CurecallModalContainer,
  ECurecallSize,
  EyeIcon,
} from '@honestica/curecall-ui'
import { useFormContext } from 'react-hook-form'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ReactQuillDynamic } from './react-quill-editor'
import { twMerge } from 'src/utils/tw-merge.util'
import { ContentHTMLPreviewModal } from './content-preview-modal.component'
import { useLocale } from 'src/hooks/use-locale'

function ContentEditor({ currentContentName }: { currentContentName: 'content.fr' | 'content.en' }) {
  const { t } = useTranslation()
  const [currentContent, setCurrentContent] = useState<any>()
  const [previewToShow, setPreviewToShow] = useState<string | null>(null)
  const range = useRef<any | null>(null)
  const [showImageModal, setShowImageModal] = useState<boolean>(false)
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false)
  const { locale } = useLocale()

  const quillRef = useRef(null)
  const imageUrlRef = useRef(null)
  const videoUrlRef = useRef(null)

  const {
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext()

  const isInitializingRef = useRef(true)

  useEffect(() => {
    if (currentContentName) {
      isInitializingRef.current = true
      const contentValue = getValues(currentContentName)
      setCurrentContent(contentValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContentName])

  const onChangeContent = (data: string) => {
    // Quill fires onChange during initialization before rendering the actual value.
    // Ignore that initial synthetic event to avoid overwriting the form value.
    if (isInitializingRef.current) {
      isInitializingRef.current = false
      return
    }
    const cleanedData = data.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ') // remove &nbsp; on paste because this type of space cause responsive issue
    setCurrentContent(data)
    // by default Quill add <p> in content even if there is nothing inside
    if (cleanedData === '<p><br></p>') {
      setValue(currentContentName, undefined)
    } else {
      setValue(currentContentName, cleanedData)
    }
    clearErrors('content')
    if (quillRef?.current) {
      const editor = (quillRef.current as any).getEditor()
      range.current = editor.getSelection()
    }
  }

  const insertImage = () => {
    const imageUrlInput = imageUrlRef.current as null | HTMLInputElement
    if (!quillRef?.current || !imageUrlInput) return
    const editor = (quillRef?.current as any).getEditor()
    editor.insertEmbed(range.current?.index, 'image', imageUrlInput.value, 'user')
    setShowImageModal(false)
  }

  const insertVideo = () => {
    const videoUrlInput = videoUrlRef.current as null | HTMLInputElement
    if (!quillRef?.current || !videoUrlInput?.value) {
      setShowVideoModal(false)
      return
    }
    const url = videoUrlInput.value
    const editor = (quillRef.current as any).getEditor()

    if (range.current?.index) {
      editor.insertEmbed(range.current.index, 'custom-video', url)
    } else {
      const editorEnd = editor.getLength()
      editor.insertEmbed(editorEnd, 'custom-video', url)
      editor.insertText(editorEnd + 1, '') // help to delete video
    }

    setShowVideoModal(false)
  }

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
          ['link', 'image', 'video'],
        ],
        handlers: { image: () => setShowImageModal(true), video: () => setShowVideoModal(true) },
      },
      imageResize: { modules: ['Resize', 'DisplaySize'] },
    }),
    [],
  )
  return (
    <div className="c-relative c-grow c-mt-10 c-flex c-flex-col">
      <ReactQuillDynamic
        forwardedRef={quillRef}
        value={currentContent}
        onChange={onChangeContent}
        modules={modules}
        className={twMerge('c-w-full c-grow c-break-all', locale === 'fr' && 'quill-fr')} // quill-fr allow to translate label in fr
      />
      <button
        type="button"
        onClick={() => setPreviewToShow(currentContent)}
        className="c-absolute c-top-2 c-right-3 c-text-primary c-flex c-items-center"
      >
        <EyeIcon className="tw-w-4 tw-mr-2" />
        {t('pages.configuration.contents.preview')}
      </button>

      {errors['content'] && (
        <CurecallInputError errorClassName="tw-relative" errorMessage={errors['content'].message as string} />
      )}

      {previewToShow && (
        <ContentHTMLPreviewModal previewToShow={previewToShow} setPreviewToShow={setPreviewToShow} />
      )}

      <CurecallModalContainer
        closeModalAriaLabel={t('common.actions.close')}
        title={t('pages.configuration.contents.enter-image-url')}
        isOpen={showImageModal}
        toggle={() => setShowImageModal((prev) => !prev)}
      >
        <div className="c-flex c-gap-4">
          <CurecallInput ref={imageUrlRef} inputClassName="tw-w-80" />
          <CurecallButton size={ECurecallSize.SMALL} type="button" onClick={insertImage}>
            {t('pages.configuration.contents.validate')}
          </CurecallButton>
        </div>
      </CurecallModalContainer>

      <CurecallModalContainer
        closeModalAriaLabel={t('common.actions.close')}
        title={t('pages.configuration.contents.enter-video-url')}
        isOpen={showVideoModal}
        toggle={() => setShowVideoModal((prev) => !prev)}
      >
        <div className="c-flex c-gap-4">
          <CurecallInput ref={videoUrlRef} inputClassName="tw-w-80" />
          <CurecallButton size={ECurecallSize.SMALL} type="button" onClick={insertVideo}>
            {t('pages.configuration.contents.validate')}
          </CurecallButton>
        </div>
      </CurecallModalContainer>
    </div>
  )
}

export { ContentEditor }
